import { PropsWithChildren, useState } from 'react';

import { Button } from '@breezeventures/ui-kit/button';
import { Icon } from '@breezeventures/ui-kit/next/icon';
import { Typography } from '@breezeventures/ui-kit/typography';
import cn from 'classnames';

import { InfoCardHeader } from './info-card-header';

type InfoCardProps = PropsWithChildren<{
  header: string;
  subheader?: string;
  expandable?: boolean;
  onEdit?: () => void;
}>;

export const InfoCard = ({
  header,
  subheader,
  children,
  expandable,
  onEdit,
}: InfoCardProps) => {
  const [expanded, setExpanded] = useState<boolean>(!expandable);

  const toggleExpanded = () => {
    setExpanded((current) => !current);
  };

  return (
    <div
      className={`
        overflow-hidden rounded-xl border border-neutral-200 p-4
        shadow-custom-md
      `}
    >
      <InfoCardHeader
        expandable={expandable}
        onClick={expandable ? toggleExpanded : undefined}
      >
        {expandable && (
          <Icon
            name="chevron-down"
            size={24}
            version="v1"
            className={cn(
              'shrink-0 transition-transform duration-200 ease-out',
              expanded && 'rotate-180'
            )}
          />
        )}
        <div className="flex min-w-[1px] flex-col">
          <Typography.Text size="m" weight="medium" tag="div">
            {header}
          </Typography.Text>
          {subheader && (
            <Typography.Text size="s" color="secondary" className="truncate">
              {subheader}
            </Typography.Text>
          )}
        </div>
        {onEdit && (
          <Button
            onClick={(event) => {
              event.stopPropagation();
              onEdit();
            }}
            type="button"
            size="s"
            variant="secondary"
            className="ml-auto"
            aria-label={`Edit ${header}`}
          >
            Edit
          </Button>
        )}
      </InfoCardHeader>
      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            className={cn(
              'mt-4 transition-opacity duration-200 ease-out',
              expanded ? 'opacity-100' : 'opacity-0'
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
